import { gql } from '@apollo/client/core';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Button } from 'component-lib/atoms/button/Button';
import { Form } from 'component-lib/atoms/controls/Form';
import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { Tooltip } from 'component-lib/atoms/tooltip/Tooltip';
import { Markdown } from 'component-lib/molecules/markdown/Markdown';
import { UserDetailsForm } from 'component-lib/organisms/user-details-form/UserDetailsForm';
import { PactSafe } from 'core/atoms/pact-safe/PactSafe';
import { useNotificationContext } from 'core/context/Notifications';
import embedEvent from 'play/support/embedEvent';
import DarkOverlay from 'play/templates/dark-overlay/DarkOverlay';
import { useAuthUserContext } from 'support/auth/AuthUserContext';
import { QueryParams } from 'support/QueryParams';
import {
  UserDetailsInput,
  useClaimGatedEmbedTrackInviteMutation,
  useConsentToTeamPrivacyPolicyGatedEmbedMutation,
  useGatedEmbedPageQuery,
} from 'types/graphql';

const FORM_ID = 'intruqt-user-details-form';

export const GatedEmbed = () => {
  const { inviteID = '' } = useParams<'inviteID'>();
  const { instruqtUser } = useAuthUserContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<UserDetailsInput>();
  const [didConsentToPrivacyPolicy, setDidConsentToPrivacyPolicy] = useState(false);
  const [didConsent, setDidConsent] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [params] = useSearchParams();
  const { dispatch } = useNotificationContext();

  const { data, loading } = useGatedEmbedPageQuery({ variables: { inviteID } });
  const [claimGatedEmbed, { loading: isClaiming, called: isFormSubmitted }] = useClaimGatedEmbedTrackInviteMutation();
  const [consentToTeamPrivacyPolicy] = useConsentToTeamPrivacyPolicyGatedEmbedMutation();

  const trackInvite = data?.trackInvite;
  const stepsCount = trackInvite?.team.features?.accept_terms_checkbox ? 2 : 1;

  const branding = {
    foregroundColor: trackInvite?.team.theme?.foreground_color || '',
    backgroundColor: trackInvite?.team.theme?.background_color || '',
    logo: trackInvite?.team.logo || '',
  };

  const formFields = trackInvite?.team.form?.fields;
  const formTitle = trackInvite?.team.form?.formTitle || 'To access this content, please leave your details.';
  const teamName = trackInvite?.team.name;

  const isClaimed = !!trackInvite?.currentUserClaim;
  let configPreviewMode = params.get('configPreviewMode');

  const accessGatedEmbed = async (inviteID: string, userDetails?: UserDetailsInput, privacyPolicyConsent?: boolean) => {
    if (trackInvite?.team.privacy_policy) {
      await consentToTeamPrivacyPolicy({
        variables: { teamID: trackInvite.team.id, consent: Boolean(privacyPolicyConsent) },
      });
    }

    const customParameters = Array.from(params)
      .filter(([key]) => key.startsWith('icp_'))
      .map(([key, value]) => ({ key: key.substring(4), value }));

    try {
      await claimGatedEmbed({
        variables: {
          inviteID,
          userDetails: userDetails || null,
          customParameters,
        },
      });
      embedEvent('invite.form_submitted', {
        invite_id: trackInvite?.id as string,
        track_slug: trackInvite?.team.slug as string,
      });
    } catch (e) {
      let errMsg = (e as Error)?.message;
      if (errMsg.includes('must be work email')) {
        errMsg = 'Please enter a business email. Personal email domains are not allowed.';
      }
      dispatch('error', '', errMsg);
    }
  };

  if (loading) {
    return (
      <DarkOverlay>
        <LoadingImage />
      </DarkOverlay>
    );
  }

  if (isClaimed && !configPreviewMode) {
    const teamSlug = trackInvite.team.slug;
    const trackSlug = trackInvite.tracks[0].slug;
    const challengeSlug = trackInvite.tracks[0].challenges[0].slug;

    const url = isFormSubmitted
      ? `/embed/${teamSlug}/invite/${inviteID}/tracks/${trackSlug}/challenges/${challengeSlug}/notes?${QueryParams.AUTO_START}=true`
      : `/embed/${teamSlug}/invite/${inviteID}/tracks/${trackSlug}/challenges`;

    return <Navigate to={url} replace />;
  }

  return (
    <div className="flex bg-white h-screen">
      <div
        className="w-[50%] p-4 flex flex-col gap-2 align-start overflow-y-auto"
        style={{ backgroundColor: branding.backgroundColor }}
      >
        <div>
          <img src={branding.logo} alt="" className="max-h-4" />
        </div>
        <div style={{ color: branding.foregroundColor }}>
          <h1 className="text-3xl mb-1 font-semibold">
            {trackInvite?.publicTitle || trackInvite?.tracks[0].title || `${teamName} track`}
          </h1>
          {trackInvite?.publicDescription && (
            <Markdown
              className="intro leading-snug prose mt-2 text-inherit [a]:(hover:link)"
              allowedElements={allowedElements}
              unwrapDisallowed
            >
              {trackInvite.publicDescription}
            </Markdown>
          )}
        </div>
      </div>

      <div className="relative w-[50%] overflow-y-auto">
        <div className="p-4 max-h-full overflow-x-visible pb-5 mb-5">
          <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold">{formTitle}</div>
            <div className="text-xs">
              By submitting this form, you consent to Instruqt sharing your details with {teamName}.
            </div>

            {currentStep === 1 && (
              <UserDetailsForm
                id={FORM_ID}
                fields={formFields || []}
                teamName={teamName}
                onSubmit={async (userDetails, privacyPolicyConsent) => {
                  if (stepsCount > 1) {
                    setFormData(userDetails);
                    setDidConsentToPrivacyPolicy(!!privacyPolicyConsent);
                    setCurrentStep(2);

                    return;
                  }

                  accessGatedEmbed(inviteID, userDetails, privacyPolicyConsent);
                }}
                privacyPolicyURL={trackInvite?.team.privacy_policy || undefined}
                consentNotice={trackInvite?.team.form?.consentNotice || undefined}
                validateWorkEmail={trackInvite?.team.form?.validateWorkEmail || undefined}
                className="gap-1"
                setHasError={setHasError}
              />
            )}

            {currentStep === 2 && (
              <form
                id={FORM_ID}
                onSubmit={e => {
                  e.preventDefault();

                  if (!didConsent || !formData) {
                    return;
                  }

                  accessGatedEmbed(inviteID, formData, didConsentToPrivacyPolicy);
                }}
              >
                <h3 className="display-6 text-primary mb-1">Accept Hashicorp terms</h3>

                <p className="text-primary-medium mb-3">
                  To access Hashicorp’s content, please read to the bottom of the agreement below
                </p>

                {didConsent && <p>You've already accepted the terms of service. Press submit to access.</p>}

                <PactSafe
                  userId={instruqtUser.id}
                  onValid={() => setDidConsent(true)}
                  onInvalid={() => setDidConsent(false)}
                />
              </form>
            )}

            <div className="h-14 -mb-14 pointer-events-none">
              <div className="fixed right-0 bottom-0 w-[50%] h-14 px-4 pt-6 flex flex-row items-center bg-gradient-to-t from-white via-white to-transparent">
                {stepsCount > 1 && !!currentStep && (
                  <div className="text-primary-medium text-xs">
                    Step {currentStep} of {stepsCount}
                  </div>
                )}

                <Tooltip content={currentStep === stepsCount && !!configPreviewMode ? 'Can not access in preview' : ''}>
                  <div>
                    <Button
                      type="submit"
                      form={FORM_ID}
                      variant="primary"
                      disabled={isClaiming || (currentStep === stepsCount && !!configPreviewMode)}
                      className="pointer-events-auto self-start"
                    >
                      {currentStep < stepsCount ? 'Next' : 'Access'}
                    </Button>
                  </div>
                </Tooltip>

                {hasError && (
                  <Form.Hint error style={{ marginTop: 0, marginLeft: '1rem' }}>
                    Please fix the errors in your invite to continue
                  </Form.Hint>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

gql`
  mutation ClaimGatedEmbedTrackInvite(
    $inviteID: String!
    $userDetails: UserDetailsInput
    $customParameters: [CustomParametersInput!]
  ) {
    claimTrackInvite(inviteID: $inviteID, userDetails: $userDetails, customParameters: $customParameters) {
      id

      trackInvite {
        id
        currentUserClaim {
          id
          playTTL
        }
      }
    }
  }

  mutation ConsentToTeamPrivacyPolicyGatedEmbed($teamID: String!, $consent: Boolean!) {
    consentToTeamPrivacyPolicy(teamID: $teamID, consent: $consent)
  }

  query GatedEmbedPage($inviteID: String!) {
    trackInvite(inviteID: $inviteID) {
      id
      title
      publicTitle
      publicDescription

      tracks {
        id
        title
        slug

        challenges {
          id
          slug
        }
      }

      team {
        id
        name
        logo
        privacy_policy
        slug

        form {
          formTitle
          consentNotice
          validateWorkEmail

          fields {
            id
            fieldName
            isRequired
          }
        }

        theme {
          foreground_color
          background_color
          background_image
        }

        features {
          accept_terms_checkbox
        }
      }

      currentUserClaim {
        id
        playTTL
      }
    }
  }
`;

const allowedElements = ['a', 'ul', 'ol', 'li', 'p'];
